/**
 * Aba Etapas em leitura (PR2).
 *
 * Lê `steps` defensivamente (wire-shape duplo `{ list }` vs array — R15) e
 * renderiza cada etapa como card: número · nome · objetivo · ações · critério.
 * `actions` é heterogêneo (strings vs objetos) — normalizado por formatStepAction.
 *
 * SEM drag/reordenar/editar (read-only — isso é PR4). Sem painel de validação
 * (o backend não expõe `validationIssue`). Empty-state honesto.
 */

import { Lock } from 'lucide-react';

import {
  conductorActionLabel,
  CONFIRMATION_OPTIONS,
  ENFORCEMENT_OPTIONS,
  formatStepAction,
  OBLIGATION_OPTIONS,
  readList,
} from '../playbook-labels';
import { effectiveBlockedList, hasGovernance, stepArmsGate } from '../step-governance-ui';

import type { PlaybookDto, PlaybookStep } from '../types';

/** Resumo read-only da governança de uma etapa (só quando há governança). */
function StepGovernanceSummary({ step }: { step: PlaybookStep }): JSX.Element | null {
  if (!hasGovernance(step)) return null;

  const parts: string[] = [];
  const force = ENFORCEMENT_OPTIONS.find((o) => o.value === (step.enforcement ?? 'flexivel'));
  if (force != null) parts.push(force.label);
  if (step.obligation !== undefined && step.obligation !== 'optional') {
    parts.push(
      OBLIGATION_OPTIONS.find((o) => o.value === step.obligation)?.label ?? step.obligation,
    );
  }
  if (step.confirmation !== undefined && step.confirmation !== 'none') {
    parts.push(
      CONFIRMATION_OPTIONS.find((o) => o.value === step.confirmation)?.label ?? step.confirmation,
    );
  }

  const arms = stepArmsGate(step);
  const blockedList = effectiveBlockedList(step).map(conductorActionLabel);

  return (
    <div className="pb-step-card__field">
      <span className="pb-step-card__field-label">Governança</span>
      <span className="pb-step-card__field-value">
        {parts.join(' · ')}
        {arms && (
          <span className="pb-step-gov__lock">
            <Lock size={12} /> bloqueia {blockedList.join(', ')} até {step.successSignal?.tool}
          </span>
        )}
      </span>
    </div>
  );
}

function StepCard({ step, index }: { step: PlaybookStep; index: number }): JSX.Element {
  const actions = Array.isArray(step.actions) ? step.actions : [];
  return (
    <div className="pb-step-card">
      <div className="pb-step-card__head">
        <span className="pb-step-card__num">#{index + 1}</span>
        <span className="pb-step-card__title">{step.name}</span>
      </div>
      <div className="pb-step-card__body">
        <div className="pb-step-card__field">
          <span className="pb-step-card__field-label">Objetivo</span>
          <span className="pb-step-card__field-value">
            {step.objective !== undefined && step.objective !== '' ? (
              step.objective
            ) : (
              <span className="pb-read-value--muted">— sem objetivo</span>
            )}
          </span>
        </div>
        {actions.length > 0 && (
          <div className="pb-step-card__field">
            <span className="pb-step-card__field-label">Ações</span>
            <ul className="pb-step-card__actions">
              {actions.map((action, i) => (
                <li key={i}>{formatStepAction(action)}</li>
              ))}
            </ul>
          </div>
        )}
        {step.successCriteria !== undefined && step.successCriteria !== '' && (
          <div className="pb-step-card__field">
            <span className="pb-step-card__field-label">Critério de sucesso</span>
            <span className="pb-step-card__field-value">{step.successCriteria}</span>
          </div>
        )}
        <StepGovernanceSummary step={step} />
      </div>
    </div>
  );
}

export function TabSteps({ playbook }: { playbook: PlaybookDto }): JSX.Element {
  const steps = readList<PlaybookStep>(playbook.steps);

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Etapas">
      <h2 className="pb-panel__title">Etapas</h2>

      {steps.length > 0 ? (
        <div className="pb-step-list">
          {steps.map((step, i) => (
            <StepCard key={step.id ?? i} step={step} index={i} />
          ))}
        </div>
      ) : (
        <p className="pb-read-value pb-read-value--muted">Nenhuma etapa definida</p>
      )}
    </div>
  );
}
